import { AsyncData, Result } from "@swan-io/boxed";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { StyleSheet, View } from "react-native";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { Box } from "./Box";
import { Button } from "./Button";
import { FocusTrap } from "./FocusTrap";
import { FullScreenScrollView } from "./FullScreenScrollView";
import { Space } from "./Space";
import { Text } from "./Text";

const styles = StyleSheet.create({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    cursor: "default",
    backdropFilter: "blur(4px)",
    animationKeyframes: {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
    animationDuration: animDuration[500],
  },

  overlayClosing: {
    animationKeyframes: {
      from: {
        opacity: 1,
      },
      to: {
        opacity: 0,
      },
    },
    animationFillMode: "forwards",
  },

  modalContainer: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    outlineWidth: 0,
  },

  modal: {
    backgroundColor: colors.neutral[900],
    paddingHorizontal: 24,
    paddingVertical: 24,
    borderRadius: radii[8],
    borderWidth: 1,
    borderColor: colors.neutral[700],
    outlineWidth: 0,
    animationKeyframes: {
      from: {
        opacity: 0,
        transform: "scale(0.9)",
      },
      to: {
        opacity: 1,
        transform: "scale(1)",
      },
    },
    animationDuration: animDuration[200],
  },

  modalClosing: {
    animationKeyframes: {
      from: {
        opacity: 1,
        transform: "scale(1)",
      },
      to: {
        opacity: 0,
        transform: "scale(0.9)",
      },
    },
    animationFillMode: "forwards",
  },

  confirmButton: {
    flex: 1,
  },
});

type Props = {
  maxWidth?: number;
  onClose?: () => void;
  children: React.ReactNode;
};

export const Modal: React.FC<Props> = ({ maxWidth = 530, onClose, children }) => {
  const ref = useRef<View | null>(null);
  const opened = children != null;
  const childrenRef = useRef(children);
  const onCloseRef = useRef(onClose);
  const openedRef = useRef(opened);
  const [state, setState] = useState<"hidden" | "visible" | "closing">(() =>
    opened ? "visible" : "hidden",
  );
  if (children != null) {
    childrenRef.current = children;
  }

  useEffect(() => {
    const element = ref.current as HTMLDivElement | null;
    const wasOpened = openedRef.current;
    openedRef.current = opened;

    if (opened === wasOpened) {
      return;
    }

    if (opened && !wasOpened) {
      setState("visible");
      return;
    }

    if (!opened && wasOpened && element != null) {
      const handleAnimationEnd = () => {
        setState("hidden");
      };

      setState("closing");
      element.addEventListener("animationend", handleAnimationEnd);

      return () => {
        element.removeEventListener("animationend", handleAnimationEnd);
      };
    }
  }, [opened]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (state === "visible") {
      const handleClick = (event: MouseEvent) => {
        const modal = ref.current as HTMLDivElement | null;
        if (!modal?.contains(event.target as Node)) {
          event.preventDefault();
          onCloseRef.current?.();
        }
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onCloseRef.current?.();
        }
      };

      document.addEventListener("click", handleClick);
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("click", handleClick);
        document.removeEventListener("keydown", handleEscape);
      };
    }

    if (state === "hidden") {
      childrenRef.current = null;
    }
  }, [state]);

  if (state === "hidden") {
    return null;
  }

  const closing = state === "closing";

  return createPortal(
    <>
      <View style={[styles.overlay, closing && styles.overlayClosing]} />
      <FocusTrap style={styles.modalContainer}>
        <FullScreenScrollView
          paddingHorizontal={24}
          paddingVertical={24}
          maxWidth={maxWidth}
          verticalCenter={0.5}
        >
          <View ref={ref} style={[styles.modal, closing && styles.modalClosing]}>
            {childrenRef.current}
          </View>
        </FullScreenScrollView>
      </FocusTrap>
    </>,
    document.body,
  );
};

export type ConfirmContentProps = {
  title: string;
  message: React.ReactNode;
  confirmText: string;
  cancelText: string;
  status?: AsyncData<Result<unknown, unknown>>;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ConfirmContent: React.FC<ConfirmContentProps> = ({
  title,
  message,
  confirmText,
  cancelText,
  status,
  onConfirm,
  onCancel,
}) => {
  return (
    <>
      <Text variant="title3" color={invariantColor.white}>
        {title}
      </Text>
      <Space height={8} />
      <Text variant="small">{message}</Text>

      <Space height={24} />

      <Box direction="row">
        <Button variant="outline" style={styles.confirmButton} onPress={onCancel}>
          {cancelText}
        </Button>

        <Space width={12} />

        <Button variant="plain" style={styles.confirmButton} status={status} onPress={onConfirm}>
          {confirmText}
        </Button>
      </Box>
    </>
  );
};
